import { useMemo } from 'react';
import {
  PropertyOperation,
  type CatalogProperty,
  type PrepareMeliPublicationDraftParams,
  type PropertyPortalPublication,
} from 'src/api/generated';
import { useMeliPublicationDraftPreview } from 'src/modules/shared/hooks/api/integrations/meli-publications';
import {
  getCurrentMeliPublication,
  type SupportedMeliOperation,
} from '../utils/meli-publication.util';
import { getOperationDraftParams } from '../utils/property-publishing-panel.util';

interface UsePropertyPublishingDraftPreviewsParams {
  managerOperation: SupportedMeliOperation | null;
  previewDraftsEnabled: boolean;
  property: CatalogProperty | null;
  publications: PropertyPortalPublication[];
  operations: SupportedMeliOperation[];
}

interface PropertyPublishingDraftIssue {
  /** Stable MeLi validation/error code returned by the preview. */
  code: string;
  /** Property or payload field related to the issue. */
  field: string;
  /** Human-readable issue message from the draft preview. */
  message: string;
}

interface PropertyPublishingDraftPreview {
  /** Blocking or advisory issues returned by the preview. */
  issues: PropertyPublishingDraftIssue[];
}

interface PropertyPublishingDraftPreviewMap {
  /** Draft preview for sale publications when supported. */
  [PropertyOperation.SALE]: PropertyPublishingDraftPreview | null;
  /** Draft preview for rent publications when supported. */
  [PropertyOperation.RENT]: PropertyPublishingDraftPreview | null;
}

interface PropertyPublishingDraftParamsMap {
  /** Draft parameters for sale publications when supported. */
  [PropertyOperation.SALE]: PrepareMeliPublicationDraftParams | null;
  /** Draft parameters for rent publications when supported. */
  [PropertyOperation.RENT]: PrepareMeliPublicationDraftParams | null;
}

interface PropertyPublishingDraftLoadingMap {
  /** Whether the sale draft preview is currently loading. */
  [PropertyOperation.SALE]: boolean;
  /** Whether the rent draft preview is currently loading. */
  [PropertyOperation.RENT]: boolean;
}

interface UsePropertyPublishingDraftPreviewsResult {
  /** Draft preview data keyed by operation. */
  draftByOperation: PropertyPublishingDraftPreviewMap;
  /** Draft parameters keyed by operation for row shortcuts and modal defaults. */
  draftParamsByOperation: PropertyPublishingDraftParamsMap;
  /** Loading flags keyed by operation. */
  loadingDraftByOperation: PropertyPublishingDraftLoadingMap;
}

/**
 * Sale/rent draft preview orchestration used by the publishing rail to power
 * row validation and direct publish shortcuts.
 */
export const usePropertyPublishingDraftPreviews = ({
  managerOperation,
  previewDraftsEnabled,
  property,
  publications,
  operations,
}: UsePropertyPublishingDraftPreviewsParams): UsePropertyPublishingDraftPreviewsResult => {
  const salePublication = useMemo(
    () => getCurrentMeliPublication(publications, PropertyOperation.SALE),
    [publications]
  );
  const rentPublication = useMemo(
    () => getCurrentMeliPublication(publications, PropertyOperation.RENT),
    [publications]
  );

  const saleDraftParams = useMemo<PrepareMeliPublicationDraftParams | null>(
    () =>
      property
        ? getOperationDraftParams(
            property,
            PropertyOperation.SALE,
            salePublication
          )
        : null,
    [property, salePublication]
  );
  const rentDraftParams = useMemo<PrepareMeliPublicationDraftParams | null>(
    () =>
      property
        ? getOperationDraftParams(
            property,
            PropertyOperation.RENT,
            rentPublication
          )
        : null,
    [property, rentPublication]
  );

  const { draft: saleDraft, loading: loadingSaleDraft } =
    useMeliPublicationDraftPreview(
      saleDraftParams,
      previewDraftsEnabled &&
        operations.includes(PropertyOperation.SALE) &&
        managerOperation !== PropertyOperation.SALE
    );
  const { draft: rentDraft, loading: loadingRentDraft } =
    useMeliPublicationDraftPreview(
      rentDraftParams,
      previewDraftsEnabled &&
        operations.includes(PropertyOperation.RENT) &&
        managerOperation !== PropertyOperation.RENT
    );

  const draftByOperation = useMemo(
    () => ({
      [PropertyOperation.SALE]: saleDraft,
      [PropertyOperation.RENT]: rentDraft,
    }),
    [rentDraft, saleDraft]
  );
  const draftParamsByOperation = useMemo(
    () => ({
      [PropertyOperation.SALE]: saleDraftParams,
      [PropertyOperation.RENT]: rentDraftParams,
    }),
    [rentDraftParams, saleDraftParams]
  );
  const loadingDraftByOperation = useMemo(
    () => ({
      [PropertyOperation.SALE]: loadingSaleDraft,
      [PropertyOperation.RENT]: loadingRentDraft,
    }),
    [loadingRentDraft, loadingSaleDraft]
  );

  return {
    draftByOperation,
    draftParamsByOperation,
    loadingDraftByOperation,
  };
};
